import { DialogActions, DialogContent, styled } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import Stack from '@mui/material/Stack'
import { isAxiosError } from 'axios'
import React from 'react'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import swapUserLocationService from 'shared/services/swapUserLocation'
import { useNotificationContext } from 'shared/context/Notifications'
import { getRefreshToken } from 'shared/utils/helpers/refreshToken'
import invalidateCarts from 'shared/utils/helpers/invalidateCarts'
import { useProfileQuery } from 'shared/store/useProfile'

import { NotificationInfo } from '../../../../types'

type Props = {
   modalInfo?: NotificationInfo
   handleClose: () => void
   notificationLocationUuid?: string
}

const StyledLink = styled('span')(({ theme }) => ({
   fontWeight: 'bold',
   textDecoration: 'none',
   color: theme.palette.primary.main,
   '&:hover': {
      color: theme.palette.primary.main,
   },
}))

export default function OrderNotification({ modalInfo, notificationLocationUuid, handleClose }: Props) {
   const { t } = useTranslation('NOTIFICATIONS')

   const { data: profile } = useProfileQuery()
   const navigate = useNavigate()
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   function handleLocationSwap(locationUuid: string) {
      swapUserLocationService(locationUuid)
         .then((res) => {
            if (res.status === 200) {
               getRefreshToken(queryClient)
               queryClient.invalidateQueries('userProfile')
               queryClient.invalidateQueries('userStats')
               invalidateCarts(queryClient)
            }
         })
         .then(() => {
            navigate(`/dashboard/orders-history?uuid=${modalInfo?.orderUuid}`)
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(
                  error.response?.data?.errors[0].code || t('AUTHORIZED:header.failedLocationSwap'),
                  'error'
               )
            }
         })
   }

   function handleNavigate() {
      if (!notificationLocationUuid || notificationLocationUuid === profile?.locationUuid) {
         navigate(`/dashboard/orders-history?uuid=${modalInfo?.orderUuid}`)
      } else if (notificationLocationUuid !== profile?.locationUuid) {
         if (profile?.availableLocations.find((item) => item.uuid === notificationLocationUuid)) {
            handleLocationSwap(notificationLocationUuid)
         } else {
            addNotification(t('NOTIFICATIONS:locationPermissionError'), 'error')
         }
      }
   }

   return (
      <>
         <DialogContent>
            <Stack direction="column" spacing={2}>
               <Typography variant="body1" sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {t('cartName')}:<StyledLink onClick={handleNavigate}>{modalInfo?.cartName}</StyledLink>
               </Typography>
               <Typography variant="body1">
                  {t('phoneNumber')}: {modalInfo?.phoneNumber}
               </Typography>
            </Stack>
         </DialogContent>
         <DialogActions>
            <ContainedButton variant="contained" onClick={handleClose}>
               OK
            </ContainedButton>
         </DialogActions>
      </>
   )
}
